'use client';

import { Suspense, useEffect, useId, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { AlertCircle, ArrowLeft, ArrowRight, Eye, EyeOff, Gauge, ShieldCheck } from 'lucide-react';

import { AuthenticatorSecret } from '@/components/dashboard/AuthenticatorSecret';
import { apiAsset, isPlatformHost } from '@/config';
import {
  completePasswordReset,
  confirmAuthenticatorRecovery,
  login,
  logout,
  requestAuthenticatorRecovery,
  requestPasswordReset,
  verifyTwoFactor,
  type TwoFactorChallenge,
} from '@/lib/auth-client';
import { useLocale } from '@/lib/i18n';
import { loadPublicSettings } from '@/lib/public-settings';
import { isPlatformAdmin, isStaffRole } from '@/lib/roles';
import type { StaffSession } from '@/lib/session';

const fieldClass =
  'mt-1.5 w-full rounded-xl border border-stone-200 bg-white px-3.5 py-3 text-sm text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-[#9a3412] focus:ring-2 focus:ring-[#FF8A00]/30';
const primaryButtonClass =
  'inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[#FF8A00] px-4 text-sm font-bold text-stone-950 transition hover:bg-[#ea7900] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9a3412] disabled:opacity-60';
const cardClass =
  'w-full rounded-[1.75rem] border border-stone-200 bg-white p-6 shadow-[0_24px_60px_rgba(22,20,17,0.08)] sm:p-8';

function loginError(code: string | null, translate: (key: string) => string): string | null {
  if (code === 'platform') return translate('login.error.platform');
  if (code === 'agency') return translate('login.error.agency');
  if (code) return translate('login.error.expired');
  return null;
}

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t, lang } = useLocale();
  const authError = searchParams.get('error');
  const platformHost = isPlatformHost();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [remember, setRemember] = useState(true);
  const [panel, setPanel] = useState<'sign-in' | 'forgot' | 'reset' | 'recover'>('sign-in');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [demoCode, setDemoCode] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [agencyLabel, setAgencyLabel] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    if (authError === 'platform' || authError === 'agency') {
      void logout();
    }
  }, [authError]);

  useEffect(() => {
    if (!authError) return;
    setError(loginError(authError, t));
  }, [authError, lang, t]);

  useEffect(() => {
    if (platformHost) return;
    let cancelled = false;
    loadPublicSettings().then((settings) => {
      if (cancelled) return;
      const label = settings.shortName || settings.name;
      setAgencyLabel(label.trim() === '' ? null : label);
      setLogoUrl(settings.logoUrl ? apiAsset(settings.logoUrl) : '');
    });
    return () => {
      cancelled = true;
    };
  }, [platformHost]);

  function finish(session: StaffSession) {
    const role = session.user.role;
    if (platformHost && !isPlatformAdmin(role)) {
      void logout();
      setError(t('login.error.agency'));
      return;
    }
    if (!platformHost && !isStaffRole(role)) {
      void logout();
      setError(t('login.error.platform'));
      return;
    }
    router.push(isPlatformAdmin(role) ? '/super-admin' : '/admin');
    router.refresh();
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      const result = await login(email.trim(), password, remember);
      if ('challenge' in result) {
        setChallenge(result.challenge);
        setCode('');
      } else {
        finish(result.session);
      }
    } catch (err: unknown) {
      setError(err instanceof Error && err.message.trim() !== '' ? err.message : t('login.error.credentials'));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(event: React.FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError(t('login.error.codeFormat'));
      return;
    }
    setError(null);
    setVerifying(true);
    try {
      finish(await verifyTwoFactor(challenge.challengeToken, trimmed));
    } catch (err: unknown) {
      setError(err instanceof Error && err.message.trim() !== '' ? err.message : t('login.error.code'));
    } finally {
      setVerifying(false);
    }
  }

  function resetChallenge() {
    setChallenge(null);
    setCode('');
    setError(null);
    setPanel('sign-in');
    setDemoCode(null);
  }

  async function handleForgot(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      setDemoCode(await requestPasswordReset(email.trim()));
      setPanel('reset');
      setResetCode('');
    } catch (err: unknown) {
      setError(err instanceof Error && err.message.trim() !== '' ? err.message : t('login.error.email'));
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(event: React.FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('login.error.mismatch'));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await completePasswordReset(email.trim(), resetCode.trim(), newPassword, confirmPassword);
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPanel('sign-in');
      setDemoCode(null);
      setNotice(t('login.error.resetDone'));
    } catch (err: unknown) {
      setError(err instanceof Error && err.message.trim() !== '' ? err.message : t('login.error.code'));
    } finally {
      setLoading(false);
    }
  }

  async function handleLostAuthenticator() {
    if (!challenge) return;
    setError(null);
    setVerifying(true);
    try {
      setDemoCode(await requestAuthenticatorRecovery(challenge.challengeToken));
      setPanel('recover');
      setResetCode('');
    } catch (err: unknown) {
      setError(err instanceof Error && err.message.trim() !== '' ? err.message : t('login.error.email'));
    } finally {
      setVerifying(false);
    }
  }

  async function handleRecover(event: React.FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    setError(null);
    setVerifying(true);
    try {
      const result = await confirmAuthenticatorRecovery(challenge.challengeToken, resetCode.trim());
      if ('challenge' in result) {
        setChallenge(result.challenge);
        setPanel('sign-in');
        setCode('');
        setDemoCode(null);
        return;
      }
      finish(result.session);
    } catch (err: unknown) {
      setError(err instanceof Error && err.message.trim() !== '' ? err.message : t('login.error.code'));
    } finally {
      setVerifying(false);
    }
  }

  const heading = challenge && panel === 'recover'
    ? t('login.heading.recover')
    : challenge
      ? (challenge.enrolled ? t('login.heading.twoFactor') : t('login.heading.setup'))
      : panel === 'forgot' || panel === 'reset'
        ? t('login.heading.reset')
        : t('login.heading.signIn');
  const lead = formLead(challenge, panel, t);

  return (
    <main className="theme-surface min-h-[calc(100dvh-3.5rem)] bg-[#f6f3ee] text-stone-950 lg:grid lg:grid-cols-[minmax(0,1.05fr)_minmax(24rem,32rem)]">
      <RoleBrief platformHost={platformHost} agencyLabel={agencyLabel} logoUrl={logoUrl} />

      <section className="flex items-center px-5 py-10 sm:px-10 lg:py-12">
        <div className="mx-auto w-full max-w-md">
          <div className={cardClass}>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-950">{heading}</h1>
            {lead ? <p className="mt-2 text-sm leading-6 text-stone-600">{lead}</p> : null}

            {challenge && panel === 'recover' ? (
              <form onSubmit={handleRecover} className="mt-7 space-y-4">
                <DemoCode code={demoCode} label={t('login.demoCode')} />
                <CodeField label={t('login.field.emailCode')} value={resetCode} onChange={setResetCode} autoFocus />
                <FormAlert message={error} />
                <button type="submit" disabled={verifying || resetCode.length !== 6} className={primaryButtonClass}>
                  {verifying ? t('login.verifying') : t('login.confirmCode')}
                </button>
                <TextButton onClick={() => { setPanel('sign-in'); setError(null); }}>{t('login.backAuth')}</TextButton>
              </form>
            ) : challenge ? (
              <form onSubmit={handleVerify} className="mt-7 space-y-4">
                {!challenge.enrolled && challenge.secret && challenge.otpauthUri && (
                  <AuthenticatorSecret secret={challenge.secret} otpauthUri={challenge.otpauthUri} variant="login" />
                )}
                <CodeField label={t('login.field.authenticator')} value={code} onChange={setCode} autoFocus />
                <FormAlert message={error} />
                <button type="submit" disabled={verifying || code.length !== 6} className={primaryButtonClass}>
                  <ShieldCheck size={16} />
                  {verifying ? t('login.verifying') : t('login.verify')}
                </button>
                {challenge.enrolled && (
                  <TextButton disabled={verifying} onClick={() => void handleLostAuthenticator()}>
                    {t('login.lostAuth')}
                  </TextButton>
                )}
                <button type="button" onClick={resetChallenge} className="inline-flex w-full items-center justify-center gap-1 text-sm font-medium text-stone-500 hover:text-stone-950">
                  <ArrowLeft size={14} className="rtl:rotate-180" /> {t('login.otherAccount')}
                </button>
              </form>
            ) : panel === 'forgot' || panel === 'reset' ? (
              <form onSubmit={panel === 'forgot' ? handleForgot : handleReset} className="mt-7 space-y-4">
                <TextField label={t('login.field.email')} type="email" autoComplete="email" required value={email} onChange={setEmail} />
                {panel === 'reset' && (
                  <>
                    <DemoCode code={demoCode} label={t('login.demoCode')} />
                    <CodeField label={t('login.field.emailCode')} value={resetCode} onChange={setResetCode} />
                    <PasswordField label={t('login.field.newPassword')} autoComplete="new-password" minLength={12} value={newPassword} onChange={setNewPassword} hint={t('login.hint.password')} />
                    <PasswordField label={t('login.field.confirmPassword')} autoComplete="new-password" minLength={12} value={confirmPassword} onChange={setConfirmPassword} />
                  </>
                )}
                <FormAlert message={error} />
                <button type="submit" disabled={loading} className={primaryButtonClass}>
                  {loading
                    ? (panel === 'forgot' ? t('login.sending') : t('login.saving'))
                    : (panel === 'forgot' ? t('login.sendCode') : t('login.save'))}
                </button>
                <TextButton onClick={() => { setPanel('sign-in'); setError(null); }}>{t('login.backSignIn')}</TextButton>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                <TextField label={t('login.field.email')} type="email" autoComplete="email" required autoFocus value={email} onChange={setEmail} />
                <PasswordField label={t('login.field.password')} autoComplete="current-password" required value={password} onChange={setPassword} />
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} className="size-4 rounded accent-[#FF8A00]" />
                    {t('login.remember')}
                  </label>
                  <button
                    type="button"
                    onClick={() => { setPanel('forgot'); setError(null); setDemoCode(null); }}
                    className="text-sm font-medium text-stone-500 underline-offset-4 hover:text-stone-950 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9a3412]"
                  >
                    {t('login.forgot')}
                  </button>
                </div>
                {notice && <p role="status" className="text-sm font-medium text-emerald-800">{notice}</p>}
                <FormAlert message={error} />
                <button type="submit" disabled={loading} className={primaryButtonClass}>
                  {loading ? t('login.submitting') : (
                    <>
                      <ArrowRight size={16} strokeWidth={2.5} className="rtl:rotate-180" />
                      {t('login.submit')}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function formLead(
  challenge: TwoFactorChallenge | null,
  panel: 'sign-in' | 'forgot' | 'reset' | 'recover',
  translate: (key: string) => string,
): string {
  if (challenge && panel === 'recover') return translate('login.lead.recover');
  if (challenge) return challenge.enrolled ? translate('login.lead.enrolled') : translate('login.lead.setup');
  if (panel === 'forgot' || panel === 'reset') return translate('login.lead.reset');
  return '';
}

function RoleBrief({ platformHost, agencyLabel, logoUrl }: { platformHost: boolean; agencyLabel: string | null; logoUrl: string }) {
  const { t } = useLocale();
  const photo = platformHost ? 'fleet-row' : 'handover-keys';
  const alt = t(platformHost ? 'landing.photo.fleet' : 'landing.photo.keys');

  return (
    <aside className="relative isolate min-h-80 overflow-hidden lg:min-h-full">
      <img
        src={`/marketing/${photo}.webp`}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-[linear-gradient(to_top,#161411_0%,rgb(22_20_17/0.94)_46%,rgb(22_20_17/0.2)_100%)]" />
      <div className="relative flex min-h-80 flex-col justify-end px-6 py-8 text-white sm:px-10 lg:min-h-full lg:px-12 lg:py-12">
        <BrandMark label={platformHost ? 'fleetava' : (agencyLabel ?? 'fleetava')} logoUrl={platformHost ? '' : logoUrl} />
        <p className="mt-6 text-sm font-semibold text-[#FF8A00]">
          {platformHost ? t('login.platform.kicker') : t('login.agency.kicker')}
        </p>
        <h2 className="mt-2 max-w-lg text-3xl font-semibold tracking-tight sm:text-4xl">
          {platformHost ? t('login.platform.title') : t('login.agency.title')}
        </h2>
        <p className="mt-3 max-w-md text-sm leading-6 text-stone-200">
          {platformHost ? t('login.platform.body') : t('login.agency.body')}
        </p>
        {platformHost ? (
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-stone-200">
            <StatusDot label={t('login.platform.registry')} />
            <span>{t('login.platform.impersonation')}</span>
            <span>{t('login.platform.audited')}</span>
          </div>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <RoleLine title={t('login.agency.owner')} detail={t('login.agency.ownerDetail')} />
            <RoleLine title={t('login.agency.desk')} detail={t('login.agency.deskDetail')} />
          </div>
        )}
        <p className="mt-6 text-sm text-stone-300">
          {platformHost ? (
            <Link href="/" className="underline-offset-4 hover:text-white hover:underline">{t('login.platform.back')}</Link>
          ) : (
            <>
              {t('login.agency.renting')}{' '}
              <Link href="/account" className="underline-offset-4 hover:text-white hover:underline">{t('login.agency.account')}</Link>
              {' · '}
              <Link href="/" className="underline-offset-4 hover:text-white hover:underline">{t('login.agency.site')}</Link>
            </>
          )}
        </p>
      </div>
    </aside>
  );
}

function BrandMark({ label, logoUrl }: { label: string; logoUrl: string }) {
  return (
    <div className="inline-flex items-center gap-2.5">
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-9 w-9 rounded-xl bg-white object-contain" />
      ) : (
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FF8A00] text-stone-950">
          <Gauge size={18} />
        </span>
      )}
      <span className="text-lg font-bold tracking-tight text-white">{label}</span>
    </div>
  );
}

function RoleLine({ title, detail }: { title: string; detail: string }) {
  return (
    <p className="rounded-2xl bg-white/8 px-3 py-2.5">
      <span className="block text-sm font-semibold text-white">{title}</span>
      <span className="mt-0.5 block text-xs leading-4 text-stone-300">{detail}</span>
    </p>
  );
}

function StatusDot({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 font-medium text-emerald-300">
      <span className="size-2 rounded-full bg-emerald-400" />
      {label}
    </span>
  );
}

function TextField({
  label,
  type,
  autoComplete,
  required,
  autoFocus,
  value,
  onChange,
}: {
  label: string;
  type: 'email' | 'text';
  autoComplete: string;
  required?: boolean;
  autoFocus?: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-stone-600">{label}</label>
      <input
        id={id}
        type={type}
        required={required}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={fieldClass}
      />
    </div>
  );
}

function PasswordField({
  label,
  autoComplete,
  required,
  minLength,
  value,
  onChange,
  hint,
}: {
  label: string;
  autoComplete: string;
  required?: boolean;
  minLength?: number;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  const id = useId();
  const { t } = useLocale();
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-stone-600">{label}</label>
      <span className="relative mt-1.5 block">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          required={required}
          minLength={minLength}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`${fieldClass} mt-0 pe-12`}
        />
        <button
          type="button"
          aria-pressed={visible}
          aria-label={visible ? t('login.hidePassword') : t('login.showPassword')}
          onClick={() => setVisible((current) => !current)}
          className="absolute inset-y-0 end-0 flex w-11 items-center justify-center text-stone-500 hover:text-stone-950"
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </span>
      {hint && <p className="mt-1 text-xs text-stone-500">{hint}</p>}
    </div>
  );
}

function CodeField({ label, value, onChange, autoFocus }: { label: string; value: string; onChange: (value: string) => void; autoFocus?: boolean }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-stone-600">{label}</label>
      <input
        id={id}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        required
        autoFocus={autoFocus}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
        className={`${fieldClass} text-center text-lg tracking-[0.4em]`}
      />
    </div>
  );
}

function FormAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="flex items-start gap-2 text-sm font-medium text-red-700">
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      {message}
    </p>
  );
}

function DemoCode({ code, label }: { code: string | null; label: string }) {
  if (!code) return null;
  return <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-stone-950">{label} {code}</p>;
}

function TextButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} className="w-full text-sm font-medium text-stone-500 hover:text-stone-950 disabled:opacity-60">
      {children}
    </button>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
